import { z } from 'zod';
import { amountMinorSchema, civilDateSchema, currencyCodeSchema, isoTimestampSchema, optionalTextSchema, reminderStepDaysSchema, SMALLINT_MAX, uuidSchema, } from './common.js';
export const paymentKindSchema = z.enum(['auto_debit', 'manual']);
export const amountModeSchema = z.enum(['fixed', 'variable']);
export const intervalUnitSchema = z.enum(['month', 'year']);
export const paymentStatusSchema = z.enum(['active', 'paused', 'cancelled']);
const intervalCountSchema = z.int().min(1).max(SMALLINT_MAX);
const anchorDaySchema = z.int().min(1).max(31);
const anchorMonthSchema = z.int().min(1).max(12);
/**
 * The mini app renders this as a link, so anything but http(s) - a "javascript:" URL above all -
 * has no business reaching an href.
 */
const cancelUrlSchema = z.url({ protocol: /^https?$/ });
/**
 * The raw shape stays separate from the checked body schemas below: refinements attached to an
 * object schema make it impossible to spread or extend, and paymentSchema is built from this very
 * shape.
 */
const paymentBodyShape = {
    name: z.string().min(1),
    kind: paymentKindSchema,
    amountMode: amountModeSchema,
    amountMinor: amountMinorSchema.nullable(),
    currency: currencyCodeSchema,
    intervalUnit: intervalUnitSchema,
    intervalCount: intervalCountSchema,
    anchorDay: anchorDaySchema,
    anchorMonth: anchorMonthSchema.nullable(),
    startDate: civilDateSchema,
    endDate: civilDateSchema.nullable(),
    cardId: uuidSchema.nullable(),
    categoryId: uuidSchema.nullable(),
    cancelUrl: cancelUrlSchema.nullable(),
    note: optionalTextSchema,
};
const priceChangeSchema = z.object({
    previousAmountMinor: amountMinorSchema,
    changedOn: civilDateSchema,
});
const amountRangeSchema = z.object({
    minMinor: amountMinorSchema,
    maxMinor: amountMinorSchema,
});
export const paymentSchema = z.object({
    id: uuidSchema,
    ...paymentBodyShape,
    status: paymentStatusSchema,
    pausedFrom: civilDateSchema.nullable(),
    resumeOn: civilDateSchema.nullable(),
    cancelledAt: isoTimestampSchema.nullable(),
    cancelReason: z.string().min(1).nullable(),
    leadDays: reminderStepDaysSchema.nullable(),
    isFromBank: z.boolean(),
    isNew: z.boolean(),
    priceChange: priceChangeSchema.nullable(),
    amountRange: amountRangeSchema.nullable(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const paymentListSchema = z.array(paymentSchema);
const paymentBodySchema = z.object(paymentBodyShape);
const hasAmountMatchingItsMode = (body) => {
    const isFixedWithAmount = body.amountMode === 'fixed' && body.amountMinor !== null;
    const isVariableWithoutAmount = body.amountMode === 'variable' && body.amountMinor === null;
    return isFixedWithAmount || isVariableWithoutAmount;
};
const hasAnchorMonthWhenYearly = (body) => body.intervalUnit !== 'year' || body.anchorMonth !== null;
/**
 * Zero padded YYYY-MM-DD compares correctly as plain text; the comparison below breaks the moment
 * civilDateSchema stops enforcing that shape.
 */
const hasEndDateNotBeforeStart = (body) => body.endDate === null || body.endDate >= body.startDate;
const withBodyChecks = (schema) => schema
    .refine(hasAmountMatchingItsMode, {
    message: 'a fixed payment carries an amount, a variable one carries none',
    path: ['amountMinor'],
})
    .refine(hasAnchorMonthWhenYearly, {
    message: 'a yearly payment needs the month it falls in',
    path: ['anchorMonth'],
})
    .refine(hasEndDateNotBeforeStart, {
    message: 'the end date cannot be earlier than the start date',
    path: ['endDate'],
});
const pastPaymentSchema = z.object({
    dueDate: civilDateSchema,
    amountMinor: amountMinorSchema,
});
const hasEachPastDateOnce = (body) => {
    const dates = [...body.pastPayments.map((payment) => payment.dueDate), ...body.skippedDates];
    return new Set(dates).size === dates.length;
};
/**
 * A subscription entered after the fact, with its history. Every period already behind today that
 * `pastPayments` names is confirmed on its own date for its own amount; every one `skippedDates`
 * names never happened and is taken off. A past period named in neither stays a debt, exactly as
 * it would without a history. Only creation takes these - an update has no past to fill in.
 */
export const createPaymentBodySchema = withBodyChecks(z.object({
    ...paymentBodyShape,
    pastPayments: z.array(pastPaymentSchema).default([]),
    skippedDates: z.array(civilDateSchema).default([]),
})).refine(hasEachPastDateOnce, {
    message: 'a past date is either paid or skipped, and only once',
    path: ['pastPayments'],
});
export const updatePaymentBodySchema = withBodyChecks(paymentBodySchema);
const pauseBodySchema = z.object({
    pausedFrom: civilDateSchema,
    resumeOn: civilDateSchema.nullable(),
});
const hasResumeNotBeforePause = (body) => body.resumeOn === null || body.resumeOn >= body.pausedFrom;
export const pausePaymentBodySchema = pauseBodySchema.refine(hasResumeNotBeforePause, {
    message: 'the resume date cannot be earlier than the pause date',
    path: ['resumeOn'],
});
export const cancelPaymentBodySchema = z.object({
    reason: z.string().min(1).nullable(),
});
/**
 * A missing status is not "every payment": design note C10 makes the default listing active plus
 * paused, and the service is what spells that out. One status per request is the whole filter.
 */
export const listPaymentsQuerySchema = z.object({
    status: paymentStatusSchema.optional(),
});
//# sourceMappingURL=payment.js.map